import { uiSurface } from "shared/ui/uiSurface"
import { Box, Chip, Stack, Typography } from "@mui/material"
import { DataGrid, GridColDef } from "@mui/x-data-grid"
import { FC, useMemo } from "react"
import { useTranslation } from "react-i18next"
import {
  ReportEntry,
  ReportParameters,
  UniversalReport,
} from "../../apis/appApi"

const DELIVERED_EVENTS = ["CUSTOMER_DELIVERY", "PROVIDER_DELIVERY", "PICKUP"]

type TabProps = {
  entries: ReportEntry[]
  report: UniversalReport
  parameters: ReportParameters
}

export const ConformityTab: FC<TabProps> = ({
  entries,
  report,
  parameters,
}) => {
  const { t } = useTranslation()

  const deliveredEntries = useMemo(
    () =>
      entries.filter(
        (e) =>
          e.traceability && DELIVERED_EVENTS.includes(e.traceability.event),
      ),
    [entries],
  )

  const formatTime = (dateStr: string | null | undefined) => {
    if (!dateStr) return ""
    try {
      return new Date(dateStr).toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      })
    } catch {
      return ""
    }
  }

  const columns: GridColDef[] = useMemo(() => {
    const cols: GridColDef[] = [
      {
        field: "courier",
        headerName: t("reports.fields.courier"),
        width: 150,
      },
      {
        field: "reference",
        headerName: t("reports.fields.reference"),
        flex: 1,
        minWidth: 120,
      },
      {
        field: "customer",
        headerName: t("reports.fields.customer"),
        flex: 2,
        minWidth: 200,
        valueGetter: (_value: any, row: any) => {
          const c = row.client
          const a = row.address
          const name = c ? `${c.firstName} ${c.lastName}` : ""
          const addr = a ? `${a.streetNumber} ${a.streetName}, ${a.city}` : ""
          return `${name} - ${addr}`
        },
      },
      {
        field: "tags",
        headerName: t("reports.fields.tags"),
        width: 200,
        renderCell: (params: any) => (
          <Stack direction="row" spacing={0.5} flexWrap="wrap">
            {(params.row.tags || []).map((tag: any) => (
              <Chip
                key={tag.id}
                label={tag.label}
                size="small"
                sx={{
                  backgroundColor: tag.color,
                  color: "#fff",
                  fontSize: "0.7rem",
                }}
              />
            ))}
          </Stack>
        ),
      },
      {
        field: "timeConstraint",
        headerName: t("reports.fields.timeConstraint"),
        width: 130,
      },
      {
        field: "preparationTime",
        headerName: t("reports.fields.preparationTime"),
        width: 120,
      },
      {
        field: "departureTime",
        headerName: t("reports.fields.departureTime"),
        width: 120,
      },
      {
        field: "deliveryTime",
        headerName: t("reports.fields.deliveryTime"),
        width: 120,
      },
      {
        field: "deliveryMessage",
        headerName: t("reports.fields.deliveryMessage"),
        flex: 1,
        minWidth: 150,
      },
      {
        field: "preparedBy",
        headerName: t("reports.fields.preparedBy"),
        width: 130,
      },
      {
        field: "interlocutor",
        headerName: t("reports.fields.interlocutor"),
        width: 150,
      },
      {
        field: "signature",
        headerName: t("reports.fields.signature"),
        width: 100,
        valueGetter: (_value: any, row: any) =>
          row.withSignature ? t("yes") : "",
      },
      {
        field: "imageCount",
        headerName: t("reports.fields.pictures"),
        width: 80,
        align: "center",
        headerAlign: "center",
      },
    ]

    if (parameters.appConfig.orderExternalReferenceEnabled) {
      cols.splice(3, 0, {
        field: "externalReference",
        headerName: t("reports.fields.externalReference"),
        width: 150,
      })
    }

    if (report.providerSize > 1) {
      cols.push({
        field: "providerName",
        headerName: t("reports.fields.provider"),
        width: 150,
      })
    }

    return cols
  }, [t, parameters, report.providerSize])

  const gridRows = useMemo(
    () =>
      deliveredEntries.map((e) => ({
        id: e.order.id,
        courier: e.courier
          ? `${e.courier.lastName} ${e.courier.firstName}`
          : "",
        reference: e.order.reference,
        client: e.order.client,
        address: e.order.address,
        tags: e.tags,
        timeConstraint: e.order.timeConstraint
          ? `${e.order.timeConstraint.start} - ${e.order.timeConstraint.end}`
          : "",
        preparationTime: formatTime(e.creationTraceability?.dateTime),
        departureTime: formatTime(e.departureTraceability?.dateTime),
        deliveryTime: formatTime(e.traceability?.dateTime),
        deliveryMessage: e.traceability?.message || "",
        preparedBy: e.creationTraceability?.operatorCode || "",
        interlocutor: e.traceability?.interlocutor?.identity || "",
        withSignature: e.traceability?.withSignature || false,
        imageCount: e.imageCount,
        externalReference: e.order.externalReference,
        providerName: e.order.providerName,
      })),
    [deliveredEntries],
  )

  return (
    <Box
      {...uiSurface("UI-DLV2-194")}
      data-testid="conformity-tab"
      sx={{ display: "flex", flexDirection: "column", gap: 2 }}
    >
      <Typography variant="subtitle1" fontWeight="bold">
        {t("reports.conformity.title")} ({deliveredEntries.length})
      </Typography>
      {deliveredEntries.length > 0 ? (
        <DataGrid
          data-testid="conformity-grid"
          rows={gridRows}
          columns={columns}
          pageSizeOptions={[25, 50, 100]}
          initialState={{ pagination: { paginationModel: { pageSize: 100 } } }}
          autoHeight
          disableRowSelectionOnClick
          density="compact"
        />
      ) : (
        <Typography data-testid="conformity-empty" color="text.secondary">
          {t("reports.empty.orders")}
        </Typography>
      )}
    </Box>
  )
}
